import { useEffect, useRef } from 'react';
import type { Boucle } from '../../partage/api/types';
import { disposer, filDe } from './dessinDeLaBoucle';
import { IssuesDeLaBoucle } from './IssuesDeLaBoucle';
import { tourDeBoucle } from './tourDeBoucle';
import { useAuPouce } from '../../partage/surface';
import { enLettres, MOTS_BOUCLE_AU_DEPLACEMENT, motsDuDossier, NE_PAS_DEPLACER } from '../../partage/vocabulaire';
import { teinteOuAccent } from '../../apparence';
import './RefusDeCycle.css';

/**
 * Ce qui a fermé la boucle. Un **lien** garde ses trois issues ; un **déplacement** n'en a
 * qu'une, « Ne pas déplacer » (décision d'Igor du 2026-10-01, chantier `deplacer`) : la
 * condition tentée est verticale, il n'y a ni lien à ne pas poser ni rangement à refaire.
 */
type Geste =
  | {
      geste: 'lien';
      /** Une issue est en train de s'appliquer : on ne la relance pas deux fois. */
      enCours: boolean;
      echec: boolean;
      /** Retirer une condition déjà posée de la boucle, puis poser le lien tenté. */
      onRetirer: (lienId: string) => void;
      /** La remplacer par une nouvelle bulle, puis poser le lien tenté. */
      onInserer: (lienId: string, titre: string) => void;
    }
  | { geste: 'deplacement' };

/**
 * M2 · Le refus d'un cycle, tranché en docs/adr/0004.
 *
 * **Ce n'est pas une erreur, c'est un renseignement.** Aucun rouge, aucun message d'échec :
 * le refus dit *« ce n'est pas une chaîne, c'est une boucle »*, il la **dessine**, et il
 * rappelle qu'une boucle ne se casse jamais de l'intérieur.
 *
 * Le dessin est le sujet de l'écran, pas son décor : une liste de trois lignes n'apprend
 * rien, un cercle qui se referme apprend tout. Les arcs sont ambrés — la boucle est ce que
 * le monde t'oppose — et **le dernier est en pointillé** : c'est le lien que tu poses.
 *
 * Il sortira rarement. C'est exactement pour ça qu'il occupe le centre de l'écran plutôt
 * qu'un coin discret : la première fois doit apprendre quelque chose, sinon il ne mérite
 * pas d'exister.
 *
 * Les trois issues sont montrées à égalité, et **la troisième est presque toujours la
 * vraie** — le maillon qu'on paye pour sortir.
 *
 * Au déplacement, la même carte et le même dessin : le pointillé est la condition que le
 * rangement poserait (la bulle d'arrivée attendrait ce qu'on y range), et une seule issue,
 * qui ferme sans rien faire.
 */
export function RefusDeCycle({
  boucle,
  onFermer,
  ...issues
}: {
  boucle: Boucle;
  onFermer: () => void;
} & Geste) {
  const m = motsDuDossier(useAuPouce());
  const tour = tourDeBoucle(boucle);
  const plan = disposer(tour.length);
  const auDeplacement = issues.geste === 'deplacement';
  const rangee = boucle.boucle.find((m) => m.id === boucle.tente.sourceId)?.titre ?? '';
  /*
   * Au déplacement, le focus va sur la seule issue, sans faire défiler le panneau : au
   * téléphone, il descendrait jusqu'au bouton et cacherait le titre et la phrase.
   */
  const seuleIssue = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (auDeplacement) seuleIssue.current?.focus({ preventScroll: true });
  }, [auDeplacement]);
  const aucuneDes = (
    <span>aucune des {enLettres(tour.length)} ne pourrait jamais commencer</span>
  );

  return (
    <>
      <div className="cycle-voile" onClick={onFermer} aria-hidden="true" />

      <div
        className="cycle"
        role="dialog"
        aria-label={auDeplacement ? MOTS_BOUCLE_AU_DEPLACEMENT.aria : 'ce lien refermerait une boucle'}
      >
        <header className="cycle-tete">
          <div>
            <p className="cycle-amorce">Ce n’est pas une erreur, c’est un renseignement</p>
            <h2>Ce n’est pas une chaîne, c’est une boucle.</h2>
          </div>
          <button
            type="button"
            className="cycle-fermer"
            onClick={onFermer}
            aria-label="fermer"
          >
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
              <path d="M3.5 3.5l10 10M13.5 3.5l-10 10" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
        </header>

        <p className="cycle-explication">
          {auDeplacement ? (
            MOTS_BOUCLE_AU_DEPLACEMENT.explication(rangee)
          ) : (
            <>Poser ce lien refermerait le cercle&nbsp;: chacune attendrait la suivante, et </>
          )}
          {aucuneDes}.
        </p>

        {/* La boucle, dessinée. Le cadre défile plutôt que de comprimer le dessin : un
            cercle écrasé cesse de se lire comme un cercle. */}
        <div className="cycle-cadre">
          <div
            className="cycle-scene"
            style={{ width: `${plan.largeur}px`, height: `${plan.hauteur}px` }}
          >
            <svg
              width={plan.largeur}
              height={plan.hauteur}
              viewBox={`0 0 ${plan.largeur} ${plan.hauteur}`}
              fill="none"
              aria-hidden="true"
            >
              {plan.arcs.map((arc, i) => (
                <g key={i} stroke="var(--monde)" strokeWidth="1.8" fill="none">
                  {/* Le dernier arc referme le cercle : c'est celui qu'on essaie de poser. */}
                  <path
                    d={arc.trace}
                    strokeDasharray={i === plan.arcs.length - 1 ? '5 5' : undefined}
                  />
                  <path d={arc.pointe} />
                </g>
              ))}
            </svg>

            <ol className="cycle-maillons">
              {tour.map((maillon, i) => {
                const place = plan.places[i];
                if (!place) return null;
                const attendu = tour[(i + 1) % tour.length];

                return (
                  <li
                    key={maillon.id}
                    className={`cycle-maillon${i === tour.length - 1 ? ' est-posee' : ''}`}
                    style={{ left: `${place.x}px`, top: `${place.y}px` }}
                  >
                    <p className="cycle-maillon-titre">{maillon.titre}</p>
                    {/* Où vit ce maillon : sans le fil, on voit la boucle sans savoir où
                        aller la casser. La pastille porte la couleur de SON dossier. */}
                    <p
                      className="cycle-maillon-fil"
                      style={
                        maillon.couleur
                          ? { ['--maillon-dossier' as string]: teinteOuAccent(maillon.couleur) }
                          : undefined
                      }
                    >
                      <span className="cycle-maillon-puce" aria-hidden="true" />
                      <span className="cycle-maillon-chemin" title={filDe(maillon, m.unDossier)}>
                        {filDe(maillon, m.unDossier)}
                      </span>
                    </p>
                    <p className="cycle-maillon-attend">Attend&nbsp;: {attendu?.titre}</p>
                  </li>
                );
              })}
            </ol>

            <p
              className="cycle-annotation"
              style={{ left: `${plan.annotation.x}px`, top: `${plan.annotation.y}px` }}
            >
              {auDeplacement ? MOTS_BOUCLE_AU_DEPLACEMENT.annotation[0] : 'Le lien que'}
              <br />
              {auDeplacement ? MOTS_BOUCLE_AU_DEPLACEMENT.annotation[1] : 'tu poses'}
            </p>
          </div>
        </div>

        {issues.geste === 'deplacement' ? (
          /*
           * Une seule issue : rien à retirer ni à insérer depuis ici, le pas tenté est un
           * rangement. Le focus s'y pose, Entrée ferme sans rien faire.
           */
          <>
            <p className="cycle-consigne">{MOTS_BOUCLE_AU_DEPLACEMENT.consigne}</p>
            <footer className="cycle-pied">
              <p>{MOTS_BOUCLE_AU_DEPLACEMENT.pied}</p>
              <button
                ref={seuleIssue}
                className="bouton"
                data-tuto="ne-pas-deplacer"
                onClick={onFermer}
              >
                {NE_PAS_DEPLACER}
              </button>
            </footer>
          </>
        ) : (
          <>
            <p className="cycle-consigne">
              Une boucle se casse de l’extérieur — trois façons, et la troisième est la vraie
            </p>

            {/* Une nouvelle boucle (après une issue qui n'a pas suffi) repart d'un choix vierge. */}
            <IssuesDeLaBoucle
              key={tour.map((m) => m.id).join(' ')}
              tour={tour}
              enCours={issues.enCours}
              echec={issues.echec}
              onNePasPoser={onFermer}
              onRetirer={issues.onRetirer}
              onInserer={issues.onInserer}
            />

            <footer className="cycle-pied">
              <p>
                {m.unSeul}&nbsp;: les boucles les plus vicieuses sont
                administratives, pas existentielles. Personne ne les voit venir — on les découvre
                au guichet.
              </p>
              <button className="bouton" onClick={onFermer}>
                J’ai compris
              </button>
            </footer>
          </>
        )}
      </div>
    </>
  );
}
